import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

/**
 * RE Debt Refi Risk + Rate-Cap panel.
 *
 * Two stacked sections:
 *   1. Maturity-wall ladder — buckets loans by quarter, with stress inputs
 *      (refi rate, current cap rate). Surfaces DSCR-at-refi-rate and
 *      LTV-at-current-cap-rate, classifying each loan REFI_DISTRESS,
 *      REFI_AT_RISK, REFI_HEALTHY (server-side classify in
 *      RefinancingRiskService).
 *   2. Rate-cap alerts — caps expiring within N months, plus low-MTM caps if
 *      a threshold is supplied. Click-through opens the cap detail row.
 *
 * Wired to:
 *   GET  /api/credit/re-debt/risk/refi-ladder
 *   GET  /api/credit/re-debt/risk/rate-caps/alerts
 */

// ---------------- styled (match RealEstateDebtScreen dark theme) ----------------
const Section = styled.div`
  background: rgba(30, 41, 59, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
`;
const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;
const SectionTitle = styled.h3`
  color: white;
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
`;
const SectionMeta = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  letter-spacing: 0.04em;
`;
const InputRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 1rem;
`;
const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: #94a3b8;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;
const NumInput = styled.input`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: white;
  padding: 0.45rem 0.7rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', monospace;
  width: 110px;
  &:focus { outline: none; border-color: #ec4899; }
`;
const RunBtn = styled.button`
  background: linear-gradient(135deg, #ec4899, #db2777);
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;
const Kpi = styled.div<{ $tone: 'distress' | 'atRisk' | 'healthy' | 'neutral' }>`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 4px solid ${(p) =>
    p.$tone === 'distress' ? '#ef4444'
    : p.$tone === 'atRisk' ? '#f59e0b'
    : p.$tone === 'healthy' ? '#10b981'
    : '#6366f1'};
  padding: 0.85rem 1rem;
  border-radius: 8px;
`;
const KpiLabel = styled.div`
  font-size: 0.7rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
`;
const KpiValue = styled.div`
  font-size: 1.15rem;
  color: white;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
`;
const Bucket = styled.div`
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;
const BucketHead = styled.div`
  background: rgba(15, 23, 42, 0.85);
  padding: 0.6rem 1rem;
  font-size: 0.85rem;
  color: white;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const BucketCount = styled.span`
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Th = styled.th`
  background: rgba(30, 41, 59, 0.6);
  color: #94a3b8;
  text-align: left;
  padding: 0.6rem 1rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;
const Td = styled.td`
  padding: 0.55rem 1rem;
  color: white;
  font-size: 0.82rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;
const TdMono = styled(Td)`
  font-family: 'JetBrains Mono', monospace;
`;
const FlagPill = styled.span<{ $flag: string }>`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$flag === 'REFI_DISTRESS' ? 'rgba(239, 68, 68, 0.2)'
    : p.$flag === 'REFI_AT_RISK' ? 'rgba(245, 158, 11, 0.2)'
    : 'rgba(16, 185, 129, 0.2)'};
  color: ${(p) =>
    p.$flag === 'REFI_DISTRESS' ? '#fca5a5'
    : p.$flag === 'REFI_AT_RISK' ? '#fcd34d'
    : '#6ee7b7'};
`;
const Empty = styled.div`
  padding: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
  text-align: center;
`;
const ErrBox = styled.div`
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #fca5a5;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

// ---------------- types ----------------
interface RefiLoan {
  loanId: number;
  loanName: string;
  propertyType?: string;
  propertyState?: string;
  sponsor?: string;
  status?: string;
  maturityDate: string;
  monthsToMaturity?: number;
  currentPrincipal?: number;
  currentDscr?: number;
  currentLtv?: number;
  estimatedNoi?: number;
  stressedDscrAtRefiRate?: number;
  stressedLtvAtCurrentCapRate?: number;
  flag: 'REFI_DISTRESS' | 'REFI_AT_RISK' | 'REFI_HEALTHY' | string;
}

interface RefiLadder {
  asOf?: string;
  horizonDate?: string;
  forwardMonths?: number;
  stressRefiRatePct?: number;
  currentCapRatePct?: number;
  loanCount?: number;
  balanceDistress?: number;
  balanceAtRisk?: number;
  balanceHealthy?: number;
  buckets?: Record<string, RefiLoan[]>;
  error?: string;
}

interface CapAlertRow {
  id: number;
  loan_id?: number;
  loanName?: string;
  counterparty?: string;
  strike_rate?: number;
  notional?: number;
  expiration_date?: string;
  last_mark_value?: number;
  last_mark_date?: string;
}

interface CapAlertsPayload {
  asOf?: string;
  mtmThreshold?: number | null;
  lowMtm?: CapAlertRow[];
  // dynamic: expiringWithin{N}Mo: CapAlertRow[]
  [k: string]: any;
}

// ---------------- formatters ----------------
const fmtMoney = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number | null, d = 2) => (v == null ? '—' : `${Number(v).toFixed(d)}%`);
const fmtMult = (v?: number | null, d = 2) => (v == null ? '—' : `${Number(v).toFixed(d)}x`);

const RealEstateRefiRiskPanel: React.FC = () => {
  const [forwardMonths, setForwardMonths] = useState<number>(24);
  const [stressRefiRate, setStressRefiRate] = useState<number>(8.5);
  const [currentCapRate, setCurrentCapRate] = useState<number>(6.5);
  const [expiryWindow, setExpiryWindow] = useState<number>(6);
  const [mtmThreshold, setMtmThreshold] = useState<number | ''>('');

  const [ladder, setLadder] = useState<RefiLadder | null>(null);
  const [ladderLoading, setLadderLoading] = useState<boolean>(true);
  const [ladderErr, setLadderErr] = useState<string | null>(null);

  const [capAlerts, setCapAlerts] = useState<CapAlertsPayload | null>(null);
  const [capLoading, setCapLoading] = useState<boolean>(true);
  const [capErr, setCapErr] = useState<string | null>(null);

  const fetchLadder = async () => {
    setLadderLoading(true);
    setLadderErr(null);
    try {
      const r = await axios.get<RefiLadder>('/api/credit/re-debt/risk/refi-ladder', {
        params: {
          forwardMonths,
          stressRefiRatePct: stressRefiRate,
          currentCapRatePct: currentCapRate,
        },
      });
      setLadder(r.data);
      if (r.data.error) setLadderErr(r.data.error);
    } catch (e: any) {
      setLadderErr(e?.response?.data?.message || e?.message || 'Failed to load refi ladder');
    } finally {
      setLadderLoading(false);
    }
  };

  const fetchCaps = async () => {
    setCapLoading(true);
    setCapErr(null);
    try {
      const params: Record<string, string | number> = { expiryWindowMonths: expiryWindow };
      if (mtmThreshold !== '' && !Number.isNaN(Number(mtmThreshold))) {
        params.mtmThreshold = Number(mtmThreshold);
      }
      const r = await axios.get<CapAlertsPayload>('/api/credit/re-debt/risk/rate-caps/alerts', { params });
      setCapAlerts(r.data);
    } catch (e: any) {
      setCapErr(e?.response?.data?.message || e?.message || 'Failed to load rate-cap alerts');
    } finally {
      setCapLoading(false);
    }
  };

  useEffect(() => {
    fetchLadder();
    fetchCaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expiringRows: CapAlertRow[] = useMemo(() => {
    if (!capAlerts) return [];
    const key = Object.keys(capAlerts).find((k) => k.startsWith('expiringWithin'));
    return key ? (capAlerts[key] as CapAlertRow[]) ?? [] : [];
  }, [capAlerts]);

  return (
    <div>
      {/* Section 1 — Maturity ladder */}
      <Section>
        <SectionHead>
          <div>
            <SectionTitle>Refi maturity ladder</SectionTitle>
            <SectionMeta>
              Stress test: DSCR ≥ 1.20× at refi rate · LTV ≤ 75% at current cap rate.
              Buckets loans by quarter through the forward horizon.
            </SectionMeta>
          </div>
        </SectionHead>

        {ladderErr && <ErrBox>{ladderErr}</ErrBox>}

        <InputRow>
          <FieldGroup>
            Forward months
            <NumInput
              type="number"
              min={3}
              max={120}
              step={3}
              value={forwardMonths}
              onChange={(e) => setForwardMonths(Number(e.target.value))}
            />
          </FieldGroup>
          <FieldGroup>
            Stress refi rate (%)
            <NumInput
              type="number"
              step="0.25"
              value={stressRefiRate}
              onChange={(e) => setStressRefiRate(Number(e.target.value))}
            />
          </FieldGroup>
          <FieldGroup>
            Current cap rate (%)
            <NumInput
              type="number"
              step="0.25"
              value={currentCapRate}
              onChange={(e) => setCurrentCapRate(Number(e.target.value))}
            />
          </FieldGroup>
          <RunBtn onClick={fetchLadder} disabled={ladderLoading}>
            {ladderLoading ? 'Running…' : 'Recompute'}
          </RunBtn>
        </InputRow>

        {ladder && !ladder.error && (
          <>
            <KpiRow>
              <Kpi $tone="distress">
                <KpiLabel>Refi distress</KpiLabel>
                <KpiValue>{fmtMoney(ladder.balanceDistress)}</KpiValue>
              </Kpi>
              <Kpi $tone="atRisk">
                <KpiLabel>At risk (within 12 mo)</KpiLabel>
                <KpiValue>{fmtMoney(ladder.balanceAtRisk)}</KpiValue>
              </Kpi>
              <Kpi $tone="healthy">
                <KpiLabel>Healthy</KpiLabel>
                <KpiValue>{fmtMoney(ladder.balanceHealthy)}</KpiValue>
              </Kpi>
              <Kpi $tone="neutral">
                <KpiLabel>Loans in horizon</KpiLabel>
                <KpiValue>{ladder.loanCount ?? 0}</KpiValue>
              </Kpi>
            </KpiRow>

            {(!ladder.buckets || Object.keys(ladder.buckets).length === 0) && (
              <Empty>No loans maturing within {forwardMonths} months.</Empty>
            )}

            {ladder.buckets && Object.entries(ladder.buckets).map(([quarter, loans]) => (
              <Bucket key={quarter}>
                <BucketHead>
                  <span>{quarter}</span>
                  <BucketCount>
                    {loans.length} loan{loans.length === 1 ? '' : 's'} ·
                    {' '}{fmtMoney(loans.reduce((s, l) => s + (l.currentPrincipal ?? 0), 0))} aggregate
                  </BucketCount>
                </BucketHead>
                <Table>
                  <thead>
                    <tr>
                      <Th>Loan</Th>
                      <Th>Property</Th>
                      <Th>Sponsor</Th>
                      <Th>Maturity</Th>
                      <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                      <Th style={{ textAlign: 'right' }}>DSCR @ refi</Th>
                      <Th style={{ textAlign: 'right' }}>LTV @ cap</Th>
                      <Th>Flag</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.map((loan) => (
                      <tr key={loan.loanId}>
                        <Td>
                          <div style={{ fontWeight: 600 }}>{loan.loanName ?? '—'}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {loan.monthsToMaturity != null ? `${loan.monthsToMaturity} mo` : ''}
                          </div>
                        </Td>
                        <Td>
                          {loan.propertyType ?? '—'}
                          {loan.propertyState ? ` · ${loan.propertyState}` : ''}
                        </Td>
                        <Td>{loan.sponsor ?? '—'}</Td>
                        <TdMono>{loan.maturityDate}</TdMono>
                        <TdMono style={{ textAlign: 'right' }}>{fmtMoney(loan.currentPrincipal)}</TdMono>
                        <TdMono style={{ textAlign: 'right' }}>{fmtMult(loan.stressedDscrAtRefiRate)}</TdMono>
                        <TdMono style={{ textAlign: 'right' }}>{fmtPct(loan.stressedLtvAtCurrentCapRate)}</TdMono>
                        <Td><FlagPill $flag={loan.flag}>{(loan.flag || '').replace('REFI_', '')}</FlagPill></Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Bucket>
            ))}
          </>
        )}
      </Section>

      {/* Section 2 — Rate-cap alerts */}
      <Section>
        <SectionHead>
          <div>
            <SectionTitle>Rate-cap hedge alerts</SectionTitle>
            <SectionMeta>
              Caps within expiry window or with last MTM below threshold (Black-76 priced).
            </SectionMeta>
          </div>
        </SectionHead>

        {capErr && <ErrBox>{capErr}</ErrBox>}

        <InputRow>
          <FieldGroup>
            Expiry window (months)
            <NumInput
              type="number"
              min={1}
              max={24}
              step={1}
              value={expiryWindow}
              onChange={(e) => setExpiryWindow(Number(e.target.value))}
            />
          </FieldGroup>
          <FieldGroup>
            MTM threshold ($)
            <NumInput
              type="number"
              step="1000"
              value={mtmThreshold}
              onChange={(e) => setMtmThreshold(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="optional"
            />
          </FieldGroup>
          <RunBtn onClick={fetchCaps} disabled={capLoading}>
            {capLoading ? 'Loading…' : 'Refresh'}
          </RunBtn>
        </InputRow>

        <Bucket>
          <BucketHead>
            <span>Expiring within {expiryWindow} months</span>
            <BucketCount>{expiringRows.length} cap{expiringRows.length === 1 ? '' : 's'}</BucketCount>
          </BucketHead>
          {expiringRows.length === 0 ? (
            <Empty>No caps expiring in this window.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Loan</Th>
                  <Th>Counterparty</Th>
                  <Th style={{ textAlign: 'right' }}>Strike</Th>
                  <Th style={{ textAlign: 'right' }}>Notional</Th>
                  <Th>Expiry</Th>
                  <Th style={{ textAlign: 'right' }}>Last MTM</Th>
                  <Th>As of</Th>
                </tr>
              </thead>
              <tbody>
                {expiringRows.map((row) => (
                  <tr key={`exp-${row.id}`}>
                    <Td>{row.loanName ?? `Loan ${row.loan_id}`}</Td>
                    <Td>{row.counterparty ?? '—'}</Td>
                    <TdMono style={{ textAlign: 'right' }}>{fmtPct(row.strike_rate)}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(row.notional)}</TdMono>
                    <TdMono>{row.expiration_date ?? '—'}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(row.last_mark_value)}</TdMono>
                    <TdMono>{row.last_mark_date ?? '—'}</TdMono>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Bucket>

        {capAlerts?.lowMtm && capAlerts.lowMtm.length > 0 && (
          <Bucket style={{ marginTop: '1rem' }}>
            <BucketHead>
              <span>Low MTM (&lt; {fmtMoney(capAlerts.mtmThreshold ?? null)})</span>
              <BucketCount>{capAlerts.lowMtm.length} cap{capAlerts.lowMtm.length === 1 ? '' : 's'}</BucketCount>
            </BucketHead>
            <Table>
              <thead>
                <tr>
                  <Th>Loan</Th>
                  <Th>Counterparty</Th>
                  <Th style={{ textAlign: 'right' }}>Strike</Th>
                  <Th style={{ textAlign: 'right' }}>Notional</Th>
                  <Th>Expiry</Th>
                  <Th style={{ textAlign: 'right' }}>Last MTM</Th>
                </tr>
              </thead>
              <tbody>
                {capAlerts.lowMtm.map((row) => (
                  <tr key={`low-${row.id}`}>
                    <Td>{row.loanName ?? `Loan ${row.loan_id}`}</Td>
                    <Td>{row.counterparty ?? '—'}</Td>
                    <TdMono style={{ textAlign: 'right' }}>{fmtPct(row.strike_rate)}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(row.notional)}</TdMono>
                    <TdMono>{row.expiration_date ?? '—'}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(row.last_mark_value)}</TdMono>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Bucket>
        )}
      </Section>
    </div>
  );
};

export default RealEstateRefiRiskPanel;
